import type { AxiosResponse } from 'axios';
import { useContext } from 'react';

import AppContext from '@/contexts/appContext';
import type { CustomScheduleExpectationFormPayload } from '@/forms/CustomScheduleExpectationForm/types';
import type {
  AppointmentType,
  AppointmentTypeWithScheduleExpectationCycles,
  AppointmentTypeWithUnexpectedAttendances
} from '@/types/appointmentType';
import type {
  ChronologicalScheduleExpectation,
  CustomScheduleExpectation,
  CustomScheduleExpectationSequence,
  FutureAppointmentRequestForDatePayload,
  MedicalStarterSequencePayload,
  ScheduleExpectation,
  ScheduleExpectationCycle,
  StartConflict,
  StartNodes
} from '@/types/scheduleExpectation';
import ajax from '@/utils/ajax';

export const scheduleExpectationRepository = {
  BASE_PATH: '/api/dugout/schedule_expectations/',

  async listAppointmentTypeOptions({
    host,
    phaseId
  }: {
    host: string;
    phaseId: string;
  }): Promise<AppointmentType[]> {
    const response = await ajax.get({
      url: `${host}/api/dugout/appointment_types/appointment_type_options_for_phase/${phaseId}/`
    });

    return response.data;
  },

  async listAppointmentTypeScheduleExpectationCyclesWithinLookahead({
    host,
    phaseId
  }: {
    host: string;
    phaseId: string;
  }): Promise<AppointmentTypeWithScheduleExpectationCycles[]> {
    const response = await ajax.get({
      url: `${host}/api/dugout/appointment_types/schedule_expectation_cycles/${phaseId}/`
    });

    return response.data;
  },

  async getUnexpectedAppointments({
    host,
    phaseId
  }: {
    host: string;
    phaseId: string;
  }): Promise<AppointmentTypeWithUnexpectedAttendances[]> {
    const response = await ajax.get({
      url: `${host}/api/dugout/appointment_types/unexpected_appointments/${phaseId}/`
    });

    return response.data;
  },

  async listAllAppointmentTypeScheduleExpectationCycles({
    host,
    phaseId
  }: {
    host: string;
    phaseId: string;
  }): Promise<AppointmentTypeWithScheduleExpectationCycles[]> {
    const response = await ajax.get({
      url: `${host}/api/dugout/appointment_types/all_schedule_expectation_cycles/${phaseId}/`
    });

    return response.data;
  },

  async getScheduleExpectation({
    host,
    phaseId,
    appointmentTypeId
  }: {
    host: string;
    phaseId: string;
    appointmentTypeId: string;
  }): Promise<ScheduleExpectation> {
    const response = await ajax.get({
      url: `${host}${this.BASE_PATH}${phaseId}/${appointmentTypeId}/`
    });

    return response.data;
  },

  async getScheduleExpectationEndCycleNodes({
    host,
    customScheduleExpectationId
  }: {
    host: string;
    customScheduleExpectationId: string;
  }): Promise<StartNodes> {
    const response = await ajax.get({
      url: `${host}/api/dugout/custom_schedule_expectations/${customScheduleExpectationId}/end_nodes/`
    });

    return response.data;
  },

  async createCustomScheduleExpectation({
    host,
    data
  }: {
    host: string;
    data: CustomScheduleExpectationFormPayload;
  }): Promise<ScheduleExpectation> {
    const response = await ajax.post<
      CustomScheduleExpectationFormPayload,
      AxiosResponse<ScheduleExpectation>
    >({
      url: `${host}/api/dugout/custom_schedule_expectations/`,
      data
    });

    return response.data;
  },

  async updateCustomScheduleExpectation({
    host,
    id,
    data
  }: {
    host: string;
    id: string;
    data: { end_at: string };
  }): Promise<ScheduleExpectation> {
    const response = await ajax.patch<{ end_at: string }, AxiosResponse<ScheduleExpectation>>({
      url: `${host}/api/dugout/custom_schedule_expectations/${id}/end/`,
      data
    });

    return response.data;
  },

  async deleteCustomScheduleExpectation({ host, id }: { host: string; id: string }): Promise<void> {
    const response = await ajax.delete({
      url: `${host}/api/dugout/custom_schedule_expectations/${id}/`
    });

    return response.data;
  },

  async listCustomScheduleExpectations({
    host
  }: {
    host: string;
  }): Promise<CustomScheduleExpectation[]> {
    const response = await ajax.get({
      url: `${host}/api/dugout/custom_schedule_expectations/`
    });

    return response.data;
  },

  async listActiveCustomScheduleExpectations({
    host,
    phaseId
  }: {
    host: string;
    phaseId: string;
  }): Promise<CustomScheduleExpectation[]> {
    const response = await ajax.get({
      url: `${host}/api/dugout/custom_schedule_expectations/`,
      params: { phase_id: phaseId, active: true }
    });

    return response.data;
  },

  async listOpenCustomScheduleExpectations({
    host,
    phaseId
  }: {
    host: string;
    phaseId: string;
  }): Promise<CustomScheduleExpectation[]> {
    const response = await ajax.get({
      url: `${host}/api/dugout/custom_schedule_expectations/`,
      params: { phase_id: phaseId, open: true }
    });

    return response.data;
  },

  async listScheduleExpectations({
    host,
    phaseId,
    appointmentTypeId
  }: {
    host: string;
    phaseId?: string;
    appointmentTypeId?: string;
  }): Promise<ScheduleExpectation[]> {
    const params: { [key: string]: string } = {};
    if (phaseId) params.phase_id = phaseId;
    if (appointmentTypeId) params.appointment_type_id = appointmentTypeId;

    const response = await ajax.get({
      url: `${host}${this.BASE_PATH}`,
      params
    });

    return response.data;
  },

  async getNextScheduledCycle({
    host,
    phaseId,
    appointmentTypeHealthieId
  }: {
    host: string;
    phaseId: string;
    appointmentTypeHealthieId: string;
  }): Promise<ScheduleExpectationCycle | null> {
    const response = await ajax.get({
      url: `${host}/api/dugout/schedule_expectation_cycles/next_scheduled/${phaseId}/${appointmentTypeHealthieId}/`
    });

    return response.data;
  },

  async recomputeScheduleExpectationsForPerson({
    host,
    personId
  }: {
    host: string;
    personId: string;
  }): Promise<{ message: string; person_id: string }> {
    const response = await ajax.post<
      { person_id: string },
      AxiosResponse<{ message: string; person_id: string }>
    >({
      url: `${host}${this.BASE_PATH}recompute-for-person/`,
      data: { person_id: personId }
    });

    return response.data;
  },

  async listChronologicalScheduleExpectations({
    host,
    phaseId,
    appointmentTypeId
  }: {
    host: string;
    phaseId: string;
    appointmentTypeId: string;
  }): Promise<ChronologicalScheduleExpectation[]> {
    const response = await ajax.get({
      url: `${host}/api/dugout/chronological_schedule_expectations/${phaseId}/${appointmentTypeId}/`
    });

    return response.data;
  },

  async listChronologicalAppointmentTypes({
    host,
    phaseId
  }: {
    host: string;
    phaseId: string;
  }): Promise<AppointmentType[]> {
    const response = await ajax.get({
      url: `${host}/api/dugout/chronological_schedule_expectations/${phaseId}/appointment_types/`
    });

    return response.data;
  },

  async createFutureAppointmentRequestForDate({
    host,
    data
  }: {
    host: string;
    data: FutureAppointmentRequestForDatePayload;
  }): Promise<ScheduleExpectation> {
    const response = await ajax.post<
      FutureAppointmentRequestForDatePayload,
      AxiosResponse<ScheduleExpectation>
    >({
      url: `${host}/api/dugout/custom_schedule_expectation_sequences/future_appointment_request_for_date/`,
      data
    });

    return response.data;
  },

  async getSequenceEndCycleNodes({
    host,
    sequenceId
  }: {
    host: string;
    sequenceId: string;
  }): Promise<StartNodes> {
    const response = await ajax.get({
      url: `${host}/api/dugout/custom_schedule_expectation_sequences/${sequenceId}/end_nodes/`
    });

    return response.data;
  },

  async endSequence({
    host,
    id,
    data
  }: {
    host: string;
    id: string;
    data: { end_at: string };
  }): Promise<CustomScheduleExpectationSequence> {
    const response = await ajax.post<
      { end_at: string },
      AxiosResponse<CustomScheduleExpectationSequence>
    >({
      url: `${host}/api/dugout/custom_schedule_expectation_sequences/${id}/end_sequence/`,
      data
    });

    return response.data;
  },

  async deleteSequence({ host, id }: { host: string; id: string }): Promise<void> {
    const response = await ajax.delete({
      url: `${host}/api/dugout/custom_schedule_expectation_sequences/${id}/`
    });

    return response.data;
  },

  async getCustomExpectationStartNodes({
    host,
    phaseId,
    appointmentTypeId
  }: {
    host: string;
    phaseId: string;
    appointmentTypeId: string;
  }): Promise<StartNodes> {
    const response = await ajax.get({
      url: `${host}/api/dugout/custom_schedule_expectations/${phaseId}/${appointmentTypeId}/start_nodes/`
    });

    return response.data;
  },

  async getMedicalStarterStartNodes({
    host,
    phaseId
  }: {
    host: string;
    phaseId: string;
  }): Promise<StartNodes> {
    const response = await ajax.get({
      url: `${host}/api/dugout/custom_schedule_expectation_sequences/${phaseId}/medical_starter_start_nodes/`
    });

    return response.data;
  },

  async getCustomExpectationStartConflict({
    host,
    phaseId,
    appointmentTypeId,
    startAt
  }: {
    host: string;
    phaseId: string;
    appointmentTypeId: string;
    startAt: string;
  }): Promise<StartConflict[]> {
    const response = await ajax.get({
      url: `${host}/api/dugout/custom_schedule_expectations/${phaseId}/${appointmentTypeId}/conflict_preflight/`,
      params: { start_at: startAt }
    });

    return response.data;
  },

  async getMedicalStarterStartConflict({
    host,
    phaseId,
    startAt
  }: {
    host: string;
    phaseId: string;
    startAt: string;
  }): Promise<StartConflict[]> {
    const response = await ajax.get({
      url: `${host}/api/dugout/custom_schedule_expectation_sequences/${phaseId}/medical_starter_conflict_preflight/`,
      params: { start_at: startAt }
    });

    return response.data;
  },

  async createMedicalStarterSequence({
    host,
    data
  }: {
    host: string;
    data: MedicalStarterSequencePayload;
  }): Promise<ScheduleExpectation> {
    const response = await ajax.post<
      MedicalStarterSequencePayload,
      AxiosResponse<ScheduleExpectation>
    >({
      url: `${host}/api/dugout/custom_schedule_expectation_sequences/create_medical_starter_sequence/`,
      data
    });

    return response.data;
  }
};

const ScheduleExpectationRepository = () => {
  const { host } = useContext(AppContext);

  return {
    async listAppointmentTypeOptions({ phaseId }: { phaseId: string }) {
      return scheduleExpectationRepository.listAppointmentTypeOptions({ host, phaseId });
    },

    async listAppointmentTypeScheduleExpectationCyclesWithinLookahead({
      phaseId
    }: {
      phaseId: string;
    }) {
      return scheduleExpectationRepository.listAppointmentTypeScheduleExpectationCyclesWithinLookahead(
        { host, phaseId }
      );
    },

    async listAllAppointmentTypeScheduleExpectationCycles({ phaseId }: { phaseId: string }) {
      return scheduleExpectationRepository.listAllAppointmentTypeScheduleExpectationCycles({
        host,
        phaseId
      });
    },

    async getUnexpectedAppointments({ phaseId }: { phaseId: string }) {
      return scheduleExpectationRepository.getUnexpectedAppointments({ host, phaseId });
    },

    async getScheduleExpectation({
      phaseId,
      appointmentTypeId
    }: {
      phaseId: string;
      appointmentTypeId?: string;
    }): Promise<ScheduleExpectation | null> {
      if (!appointmentTypeId) {
        return null;
      }

      return scheduleExpectationRepository.getScheduleExpectation({
        host,
        phaseId,
        appointmentTypeId
      });
    },

    async getScheduleExpectationEndCycleNodes({
      customScheduleExpectationId
    }: {
      customScheduleExpectationId: string;
    }) {
      return scheduleExpectationRepository.getScheduleExpectationEndCycleNodes({
        host,
        customScheduleExpectationId
      });
    },

    async createCustomScheduleExpectation({
      data
    }: {
      data: CustomScheduleExpectationFormPayload;
    }) {
      return scheduleExpectationRepository.createCustomScheduleExpectation({ host, data });
    },

    async updateCustomScheduleExpectation({ id, data }: { id: string; data: { end_at: string } }) {
      return scheduleExpectationRepository.updateCustomScheduleExpectation({ host, id, data });
    },

    async deleteCustomScheduleExpectation({ id }: { id: string }) {
      return scheduleExpectationRepository.deleteCustomScheduleExpectation({ host, id });
    },

    async listCustomScheduleExpectations() {
      return scheduleExpectationRepository.listCustomScheduleExpectations({ host });
    },

    async listActiveCustomScheduleExpectations({ phaseId }: { phaseId: string }) {
      return scheduleExpectationRepository.listActiveCustomScheduleExpectations({ host, phaseId });
    },

    async listScheduleExpectations({
      phaseId,
      appointmentTypeId
    }: {
      phaseId?: string;
      appointmentTypeId?: string;
    } = {}) {
      return scheduleExpectationRepository.listScheduleExpectations({
        host,
        phaseId,
        appointmentTypeId
      });
    },

    async getNextScheduledCycle({
      phaseId,
      appointmentTypeHealthieId
    }: {
      phaseId: string;
      appointmentTypeHealthieId: string;
    }): Promise<ScheduleExpectationCycle | null> {
      return scheduleExpectationRepository.getNextScheduledCycle({
        host,
        phaseId,
        appointmentTypeHealthieId
      });
    },

    async recomputeScheduleExpectationsForPerson({ personId }: { personId: string }) {
      return scheduleExpectationRepository.recomputeScheduleExpectationsForPerson({
        host,
        personId
      });
    },

    async listChronologicalScheduleExpectations({
      phaseId,
      appointmentTypeId
    }: {
      phaseId: string;
      appointmentTypeId: string;
    }) {
      return scheduleExpectationRepository.listChronologicalScheduleExpectations({
        host,
        phaseId,
        appointmentTypeId
      });
    },

    async listChronologicalAppointmentTypes({ phaseId }: { phaseId: string }) {
      return scheduleExpectationRepository.listChronologicalAppointmentTypes({ host, phaseId });
    },

    async createFutureAppointmentRequestForDate({
      data
    }: {
      data: FutureAppointmentRequestForDatePayload;
    }) {
      return scheduleExpectationRepository.createFutureAppointmentRequestForDate({ host, data });
    },

    async getSequenceEndCycleNodes({ sequenceId }: { sequenceId: string }) {
      return scheduleExpectationRepository.getSequenceEndCycleNodes({ host, sequenceId });
    },

    async endSequence({ id, data }: { id: string; data: { end_at: string } }) {
      return scheduleExpectationRepository.endSequence({ host, id, data });
    },

    async deleteSequence({ id }: { id: string }) {
      return scheduleExpectationRepository.deleteSequence({ host, id });
    },

    async getCustomExpectationStartNodes({
      phaseId,
      appointmentTypeId
    }: {
      phaseId: string;
      appointmentTypeId: string;
    }) {
      return scheduleExpectationRepository.getCustomExpectationStartNodes({
        host,
        phaseId,
        appointmentTypeId
      });
    },

    async getMedicalStarterStartNodes({ phaseId }: { phaseId: string }) {
      return scheduleExpectationRepository.getMedicalStarterStartNodes({ host, phaseId });
    },

    async getCustomExpectationStartConflict({
      phaseId,
      appointmentTypeId,
      startAt
    }: {
      phaseId: string;
      appointmentTypeId: string;
      startAt: string;
    }) {
      return scheduleExpectationRepository.getCustomExpectationStartConflict({
        host,
        phaseId,
        appointmentTypeId,
        startAt
      });
    },

    async getMedicalStarterStartConflict({
      phaseId,
      startAt
    }: {
      phaseId: string;
      startAt: string;
    }) {
      return scheduleExpectationRepository.getMedicalStarterStartConflict({
        host,
        phaseId,
        startAt
      });
    },

    async createMedicalStarterSequence({ data }: { data: MedicalStarterSequencePayload }) {
      return scheduleExpectationRepository.createMedicalStarterSequence({ host, data });
    }
  };
};

export default ScheduleExpectationRepository;
